import React, { useEffect, useState } from "react";
import {
    Button,
    Grid,
    Paper,
    Stack,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    IconButton,
    Tooltip,
} from "@mui/material";
import {
    FilterList as FilterListIcon,
    Clear as ClearIcon,
    Refresh as RefreshIcon,
    GetApp as ExportIcon,
} from "@mui/icons-material";

import OrdersTableComponent from "../../../components/admin/orders-manager/OrdersTable";
import OrderStatusDialog from "../../../components/admin/orders-manager/OrderStatusDialog";
import OrderDetailsDialog from "../../../components/admin/orders-manager/OrderDetailsDialog";
import { SearchRecords } from "../../../components/Common/SearchRecords";

import { useGetAdminOrdersListQuery } from "../../../store/orders/ordersApis";
import { defaultPaginate } from "../../../helpers/constants";
import { useGetRegionsDropdownQuery } from "../../../store/masters/mastersApis";

const OrdersContainer = (props) => {
    const { showDrawer, formType, initialValues, pagination, filters, selectedOrder, showOrderDetails } = props;

    const [showFilters, setShowFilters] = useState(true);
    const [selectedOrderForDetails, setSelectedOrderForDetails] = useState(null);
    const [selectedOrderForStatus, setSelectedOrderForStatus] = useState(null);

    // Build query parameters
    const queryParams = {
        page: pagination.pageNo + 1,
        limit: pagination.limit,
        sort_by: pagination.sortField,
        sort_order: pagination.sortBy,
        search: pagination.keywords,
        // Only include non-null filters
        ...(filters.payment_status && filters.payment_status !== "null" && { payment_status: filters.payment_status }),
        ...(filters.payment_type && filters.payment_type !== "null" && { payment_type: filters.payment_type }),
        ...(filters.region_id && filters.region_id !== "null" && { region_id: filters.region_id }),
        ...(filters.is_b2b && filters.is_b2b !== "null" && { is_b2b: filters.is_b2b }),
        ...(filters.start_date && filters.start_date !== "null" && { start_date: filters.start_date }),
        ...(filters.end_date && filters.end_date !== "null" && { end_date: filters.end_date }),
    };

    console.log("🔍 Admin queryParams:", queryParams);

    const { isLoading, data: orders, isFetching, refetch } = useGetAdminOrdersListQuery(queryParams);
    const { data: regionsData } = useGetRegionsDropdownQuery();

    useEffect(() => {
        props.handlePaginationState(defaultPaginate);
    }, []);

    const handlePagination = (setting) => {
        props.handlePaginationState(setting);
    };

    const handleFilterChange = (filterName, value) => {
        console.log("🔄 Admin handleFilterChange called with:", filterName, value);
        props.updateFilters({
            [filterName]: value,
        });
        props.handlePaginationState({
            ...pagination,
            pageNo: 0,
        });
    };

    const handleClearFilters = () => {
        props.resetFilters();
        props.handlePaginationState({
            ...pagination,
            keywords: "",
            pageNo: 0,
        });
    };

    const handleViewDetails = (order) => {
        console.log("handleViewDetails called with order:", order);
        setSelectedOrderForDetails(order);
        props.setShowOrderDetails(true);
    };

    const handleStatusUpdate = (order) => {
        setSelectedOrderForStatus(order);
    };

    const handleCloseDetails = () => {
        setSelectedOrderForDetails(null);
        props.setShowOrderDetails(false);
    };

    const handleCloseStatusDialog = () => {
        setSelectedOrderForStatus(null);
    };

    const handleStatusUpdateSuccess = () => {
        refetch();
    };

    const getStatusColor = (status) => {
        const statusColors = {
            pending: { bg: "#fff3cd", color: "#856404" },
            initiated: { bg: "#cce5ff", color: "#004085" },
            partial: { bg: "#ffeaa7", color: "#6c5ce7" },
            paid: { bg: "#d4edda", color: "#155724" },
            failed: { bg: "#f8d7da", color: "#721c24" },
            cancelled: { bg: "#f5c6cb", color: "#721c24" },
            refunded: { bg: "#e2e3e5", color: "#383d41" },
        };
        return statusColors[status] || { bg: "#f8f9fa", color: "#6c757d" };
    };

    const getActiveFiltersCount = () => {
        let count = 0;
        if (filters.payment_status) count++;
        if (filters.payment_type) count++;
        if (filters.region_id) count++;
        if (filters.is_b2b) count++;
        if (filters.start_date) count++;
        if (filters.end_date) count++;
        return count;
    };

    return (
        <Stack spacing={2}>
            {/* Header */}
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item xs={6} sx={{ alignSelf: "center" }}>
                    <Typography variant="h6">
                        Orders Management
                        <span style={{ color: "gray", fontSize: "10px", verticalAlign: "super", marginLeft: 8 }}>
                            ({orders?.count || 0})
                        </span>
                    </Typography>
                </Grid>
                <Grid
                    item
                    xs={6}
                    sx={{ alignSelf: "center" }}
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"flex-end"}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <SearchRecords handlePagination={handlePagination} pagination={pagination} />
                        <Tooltip title="Refresh">
                            <IconButton onClick={() => refetch()} size="small">
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Export">
                            <IconButton size="small">
                                <ExportIcon />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="outlined"
                            startIcon={<FilterListIcon />}
                            onClick={() => setShowFilters(!showFilters)}
                            size="small"
                        >
                            Filters
                            {getActiveFiltersCount() > 0 && (
                                <Chip
                                    label={getActiveFiltersCount()}
                                    size="small"
                                    sx={{
                                        ml: 1,
                                        height: 20,
                                        fontSize: "0.7rem",
                                        backgroundColor: "#1976d2",
                                        color: "white",
                                    }}
                                />
                            )}
                        </Button>
                    </Stack>
                </Grid>
            </Grid>

            {/* Filters */}
            {showFilters && (
                <Paper sx={{ p: 3, mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                        Filters
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Payment Status</InputLabel>
                                <Select
                                    value={filters.payment_status || ""}
                                    onChange={(e) => handleFilterChange("payment_status", e.target.value || null)}
                                    label="Payment Status"
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="initiated">Initiated</MenuItem>
                                    <MenuItem value="partial">Partial</MenuItem>
                                    <MenuItem value="paid">Paid</MenuItem>
                                    <MenuItem value="failed">Failed</MenuItem>
                                    <MenuItem value="cancelled">Cancelled</MenuItem>
                                    <MenuItem value="refunded">Refunded</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Payment Type</InputLabel>
                                <Select
                                    value={filters.payment_type || ""}
                                    onChange={(e) => handleFilterChange("payment_type", e.target.value || null)}
                                    label="Payment Type"
                                >
                                    <MenuItem value="">All Types</MenuItem>
                                    <MenuItem value="full">Full Payment</MenuItem>
                                    <MenuItem value="half">Half Payment</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Region</InputLabel>
                                <Select
                                    value={filters.region_id || ""}
                                    onChange={(e) => handleFilterChange("region_id", e.target.value || null)}
                                    label="Region"
                                >
                                    <MenuItem value="">All Regions</MenuItem>
                                    {regionsData?.map((region) => (
                                        <MenuItem key={region.value} value={region.value}>
                                            {region.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Order Type</InputLabel>
                                <Select
                                    value={filters.is_b2b || ""}
                                    onChange={(e) => handleFilterChange("is_b2b", e.target.value || null)}
                                    label="Order Type"
                                >
                                    <MenuItem value="">All Orders</MenuItem>
                                    <MenuItem value="true">B2B Orders Only</MenuItem>
                                    <MenuItem value="false">Regular Orders Only</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ height: "100%" }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<ClearIcon />}
                                    onClick={handleClearFilters}
                                    size="small"
                                    disabled={getActiveFiltersCount() === 0}
                                >
                                    Clear
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Orders Table */}
            <Grid container>
                <Grid item xs={12}>
                    <OrdersTableComponent
                        loading={isLoading}
                        fetching={isFetching}
                        count={orders?.count}
                        data={orders?.results || []}
                        onViewDetails={handleViewDetails}
                        onStatusUpdate={handleStatusUpdate}
                        pagination={pagination}
                        handlePagination={handlePagination}
                    />
                </Grid>
            </Grid>

            {/* Order Details Dialog */}
            <OrderDetailsDialog
                open={showOrderDetails}
                onClose={handleCloseDetails}
                orderId={selectedOrderForDetails?.id}
            />
            {/* Debug info */}
            {console.log("Dialog props:", {
                open: showOrderDetails,
                orderId: selectedOrderForDetails?.id,
                selectedOrderForDetails,
            })}

            {/* Order Status Dialog */}
            <OrderStatusDialog
                open={!!selectedOrderForStatus}
                onClose={handleCloseStatusDialog}
                order={selectedOrderForStatus}
                onSuccess={handleStatusUpdateSuccess}
            />
        </Stack>
    );
};

export default OrdersContainer;
